import CosyURLEncoder from "../helpers/CosyURLEncoder.ts";

export class VehicleOverlay {
    uid: string = "";
    vids: string[] = [];
    selectedIds = new Set<string>();
    data: any = {};
    advisor: any = {};
    overlay: HTMLDivElement | undefined = undefined;
    prevOverflow: CSSStyleDeclaration["overflow"] = "";
    isDisclaimerVisible = false;

    constructor(vids: string, uid: string) {
        this.uid = uid;
        this.vids = vids.split(",");
        if (this.vids.length) this.init().catch(console.error);
    }

    toggleDisclaimer() {
        this.isDisclaimerVisible = !this.isDisclaimerVisible;
        const disclaimerEl = this.overlay?.querySelector(".vo-disclaimer");
        if (disclaimerEl) {
            (disclaimerEl as HTMLButtonElement).style.height = this.isDisclaimerVisible ? "auto" : "0";
            (disclaimerEl as HTMLButtonElement).style.overflow = this.isDisclaimerVisible ? "auto" : "hidden";
        }

        const toggleEl = this.overlay?.querySelector(".vo-disclaimer-toggle");
        if (toggleEl) {
            (toggleEl as HTMLButtonElement).textContent = this.isDisclaimerVisible
                ? "Hide Disclaimer"
                : "Show Disclaimer";
        }
    }

    formatPrice(price: number) {
        return price?.toLocaleString("en-US", {
            style: "currency",
            currency: "USD",
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        });
    }

    async fetchAdvisorData(uid: string) {
        try {
            const response = await fetch(`https://share.autointel.ai/uid?uid=${uid}`);
            return await response.json();
        } catch (e) {
            console.warn(`Error fetching advisor data for ${uid}`);
            return {
                name: "your dealer",
                avatar: "https://cdn.auth0.com/avatars/default.png",
            };
        }
    }

    async fetchVehicleData(ids: string[]) {
        const data: {
            id: string;
            title: string;
            imageUrl: string;
            brochureUrl: string;
            leaseRangeText: string;
            msrpText: string;
        }[] = [];
        for (const id of ids) {
            if (data.length >= 4) {
                break;
            }

            try {
                const response = await fetch(`https://share.autointel.ai/inv/${id}`);
                const res: {
                    id: string;
                    name: string;
                    price: number;
                    leaseRange: { high: number; low: number };
                    financeRange: { high: number; low: number };
                    imageUrl: string;
                    orderStatus: string;
                } = await response.json();
                data.push({
                    id,
                    title: res.name,
                    imageUrl: res.imageUrl,
                    brochureUrl: `https://eve.vsr.aws.bmw.cloud/brochure/${res.id}`,
                    /* placeholder 2‑line description */
                    leaseRangeText: `${
                        res.leaseRange.low
                            ? `Lease Range: ${this.formatPrice(res.leaseRange.low)} - ${this.formatPrice(
                                  res.leaseRange.high,
                              )}`
                            : ""
                    }`,
                    msrpText: `MSRP as built: ${this.formatPrice(res.price)}\n${res.orderStatus}`,
                });
            } catch (e) {
                console.warn(`Error fetching vehicle data for ${id}`);
                data.push({
                    id,
                    title: `${id}`,
                    imageUrl: `https://prod.cosy.bmw.cloud/byo/cosySec?${CosyURLEncoder.encodeDefault(
                        `brand=WBBM&client=byo&vehicle=${id}`,
                    )}`,
                    brochureUrl: `https://eve.vsr.aws.bmw.cloud/brochure/${id}`,
                    leaseRangeText: "Vehicle Unavailable",
                    msrpText: "",
                });
            }
        }
        return data;
    }

    /* ---------- bootstrap ---------- */
    async init() {
        this.data = await this.fetchVehicleData(this.vids);
        this.advisor = await this.fetchAdvisorData(this.uid);
        this.injectStyles();
        this.render();
        requestAnimationFrame(() => this.overlay!.classList.add("vo-enter"));
    }

    injectStyles() {
        const style = document.createElement("style");
        style.textContent = /* css */ `
        :root {
            --vo-radius: 16px;

            --vo-bg: #ffffff;
            --vo-text: #0f172a;
            --vo-cta: #1C69D3;
            --vo-card-bg: #fafafa;
            --vo-border: #e5e7eb;
        }

        body {
            font-family: Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
            font-size: 16px;
            line-height: 1.5
        }

        /* backdrop */
        .vo-overlay {
            position: fixed;
            inset: 0;
            z-index: 2147483647;
            background: rgba(0, 0, 0, .55);
            backdrop-filter: blur(6px);
            display: flex;
            justify-content: center;
            align-items: flex-end;
            opacity: 0;
            pointer-events: none;
            transition: opacity .35s ease;
        }

        @media (min-width:641px) {
            .vo-overlay {
                align-items: center
            }
        }

        .vo-overlay.vo-enter {
            opacity: 1;
            pointer-events: auto
        }

        .vo-overlay.vo-enter .vo-container {
            transform: translateY(0); /* slide-up for mobile */
        }

        /* container */
        .vo-container {
            width: 100%;
            max-width: 960px;
            background: var(--vo-bg);
            color: var(--vo-text);
            border-radius: var(--vo-radius) var(--vo-radius) 0px 0px;
            box-shadow: 0 20px 40px rgba(0, 0, 0, .45), 0 0 0 1px rgba(255, 255, 255, .03);
            transform: translateY(100%);
            transition: transform .5s cubic-bezier(.22, 1, .36, 1);
            display: flex;
            flex-direction: column;
        }

        @media (min-width:641px) {
            .vo-container {
                transform: scale(.9);
                animation: vo-fade .4s ease forwards;
                border-radius: var(--vo-radius);
                max-height: max(75dvh, calc(100dvh - 512px));
                margin: 16px;
            }

            @keyframes vo-fade {
                to {
                    transform: scale(1)
                }
            }
        }

        /* header with advisor */
        .vo-header {
            position: sticky;
            top: 0;
            z-index: 1;
            background: var(--vo-bg);
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 20px 24px;
            border-bottom: 1px solid var(--vo-border);
            border-top-left-radius: var(--vo-radius);
            border-top-right-radius: var(--vo-radius);
        }

        .vo-header-left {
            display: flex;
            align-items: center;
            gap: 4px;
        }

        @media (max-width:640px) {
            .vo-header-left {
                flex-direction: column;
                align-items: flex-start;
            }
        }

        .vo-header h2 {
            margin: 0;
            font-size: 18px;
            font-weight: 600
        }

        .vo-advisor {
            display: flex;
            align-items: center;
            gap: 8px;
            font-size: 15px;
            font-weight: 500
        }

        .vo-avatar {
            width: 40px;
            height: 40px;
            border-radius: 50%;
            object-fit: cover;
            border: 2px solid var(--vo-border);
        }

        .vo-close {
            border: none;
            background: none;
            font-size: 28px;
            line-height: 1;
            width: 44px;
            height: 44px;
            display: flex;
            justify-content: center;
            align-items: center;
            color: var(--vo-text);
            cursor: pointer
        }

        /* card list */
        .vo-cards {
            display: flex;
            gap: 24px;
            overflow-x: auto;
            padding: 24px 24px calc(env(safe-area-inset-bottom) + 32px);
            scroll-snap-type: x mandatory;
            -webkit-overflow-scrolling: touch;
        }

        /* ───── Desktop card layout: perfectly centered rows ─────────────── */
        @media (min-width: 641px) {
            .vo-cards {
                /* replace the earlier grid styles */
                display: flex;
                /* ① use flexbox */
                flex-wrap: wrap;
                /* ② allow wrapping to new rows   */
                gap: 24px;
                /* ③ same gap as before           */
                justify-content: center;
                /* ④ split leftover space evenly  */
                padding: 24px;
                /* keep your 24 px inner padding  */
            }

            /* ensure every card uses the same fixed width,
                    so centering maths are predictable */
            .vo-card {
                flex: 0 1 337px;
                /* 300 px wide (shrinks on < 300) */
                max-width: 337px;
                /* keeps images crisp */
            }
        }

        /* card */
        .vo-card {
            flex: 0 0 80%;
            max-width: 375px;
            background: var(--vo-card-bg);
            border: 1px solid var(--vo-border);
            border-radius: var(--vo-radius);
            display: flex;
            flex-direction: column;
            scroll-snap-align: center;
            transition: box-shadow .25s ease, transform .25s ease;
        }
        
        .vo-card:only-child {
            margin: auto;
        }

        @media (min-width:641px) {
            .vo-card:hover {
                box-shadow: 0 8px 20px rgba(0, 0, 0, .12);
                transform: translateY(-4px)
            }
        }

        .vo-card img {
            width: 100%;
            aspect-ratio: 16/9;
            object-fit: cover;
            border-top-left-radius: var(--vo-radius);
            border-top-right-radius: var(--vo-radius);
        }

        .vo-card-content {
            padding: 16px;
            display: flex;
            flex-direction: column;
            flex: 1
        }

        .vo-card-content h3 {
            margin: 0 0 8px;
            font-size: 16px;
            font-weight: 600
        }

        .vo-desc {
            position: relative;
            font-size: 14px;
            opacity: .85;
            white-space: pre-line;
        }

        .vo-desc.mb-16 {
            margin-bottom: 16px;
        }

        .vo-links {
            margin-top: auto;
            display: flex;
            gap: 10px
        }

        .vo-links a {
            flex: 1;
            text-align: center;
            padding: 10px 0;
            font-size: 14px;
            font-weight: 500;
            border: 1px solid var(--vo-cta);
            text-decoration: none;
            transition: background .2s ease, color .2s ease
        }

        .vo-links a.brochure {
            background: var(--vo-cta);
            color: #fff
        }

        .vo-links a:active {
            filter: brightness(1.1)
        }

        /* ─────────── 1) MOBILE: slide‑up & full height ─────────── */
        @media (max-width:640px) {

            /* start off‑screen ↓ */
            .vo-container {
                transform: translateY(100%);
                height: auto;
                /* let it size naturally   */
                max-height: 90vh;
                /* but never exceed screen */
                overflow-y: auto;
                /* sheet scrolls if needed */
            }

            /* slide into view when .vo-enter hits the backdrop */
            .vo-overlay.vo-enter .vo-container {
                transform: translateY(0);
            }
        }

        /* ─────────── 2) DESKTOP: restore scale‑fade animation ───── */
        @media (min-width:641px) {

            /* initial state (before .vo-enter) */
            .vo-container {
                transform: scale(.9);
                opacity: 0;
                transition: transform .4s cubic-bezier(.22, 1, .36, 1), opacity .4s ease;
            }

            /* animate to full size / opacity once .vo-enter is added */
            .vo-overlay.vo-enter .vo-container {
                transform: scale(1);
                opacity: 1;
            }
        }
        
        /* ─── selectable cards ────────────────────────────────────────────── */
        .vo-card {
            cursor: pointer;
            /* affordance */
            position: relative;
            /* for the checkmark overlay */
        }

        .vo-check {
            position: absolute;
            top: 12px;
            right: 12px;
            width: 22px;
            height: 22px;
            border-radius: 50%;
            border: 2px solid var(--vo-cta);
            /* BMW blue ring */
            background: #fff;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 14px;
            font-weight: 700;
            color: var(--vo-cta);
            opacity: 0;
            transform: scale(.8);
            transition: opacity .2s, transform .2s;
            pointer-events: none;
            /* ignore clicks */
        }

        .vo-card.selected .vo-check {
            opacity: 1;
            transform: scale(1);
            /* fade / pop‑in */
        }
        
        /* ─── Skeleton loader for images ─────────────────────────────────── */
        .vo-img {
            position: relative;
            width: 100%;
            aspect-ratio: 16/9;
            /* reserves space */
            overflow: hidden;
            border-top-left-radius: var(--vo-radius);
            border-top-right-radius: var(--vo-radius);
        }

        .vo-img-placeholder {
            position: absolute;
            inset: 0;
            background: #e5e7eb;
            animation: pulse 1.4s ease-in-out infinite;
        }

        @keyframes pulse {

            0%,
            100% {
                opacity: .50
            }

            50% {
                opacity: .20
            }
        }

        .vo-img img {
            position: absolute;
            inset: 0;
            width: 100%;
            height: 100%;
            object-fit: cover;
            border-radius: inherit;
            /* keep corners */
            opacity: 0;
            transition: opacity .35s ease;
        }

        .vo-img.loaded img {
            opacity: 1
        }

        .vo-img.loaded .vo-img-placeholder {
            display: none
        }

        /* ─── bottom action bar ──────────────────────────────────────────── */
        .vo-footer {
            position: sticky;
            bottom: 0;
            left: 0;
            right: 0;
            z-index: 2;
            background: var(--vo-bg);
            padding: 16px 24px;
            border-top: 1px solid var(--vo-border);
            display: flex;
            justify-content: center;
            gap: 16px;
        }

        .vo-cta-btn, .vo-disclaimer-toggle {
            padding: 12px 24px;
            font-size: 16px;
            font-weight: 600;
            border-radius: 0px;
            border: none;
            font-size: 14px;
            font-weight: 500;
            cursor: pointer;
            transition: opacity .2s;
            background: var(--vo-cta);
            color: #fff;
            opacity: .4;
            pointer-events: none;
        }

        .vo-cta-btn.enabled {
            opacity: 1;
            pointer-events: auto;
        }

        .vo-cta-btn:active {
            transform: scale(.98);
        }
        
        .vo-disclaimer {
            height: 0px;
            overflow: hidden;
            background: var(--vo-bg);
            transition: height .35s ease;
            border-top: 1px solid var(--vo-border);
        }
        
        .vo-disclaimer-content {
            padding: 16px;
            max-width: 800px;
            margin: 0 auto;
            font-size: 14px;
            line-height: 1.5;
        }
        
        .vo-disclaimer-toggle {
            background: #221f1f;
            color: #fff;
            opacity: 1 !important;
            pointer-events: auto !important;
        }
        
        .vo-disclaimer-toggle:hover {
            background: #4c4c4c;
        }
        
        .vo-info {
            cursor: help;
            border-bottom: 1px dotted #4c4c4c;
        }
    `;
        document.head.appendChild(style);
    }

    render() {
        const disclaimerContent = /* HTML */ ``;

        this.overlay = Object.assign(document.createElement("div"), { className: "vo-overlay" });
        if (!this.overlay) {
            console.error("Failed to create overlay");
            return;
        }
        this.overlay.innerHTML = /* HTML */ ` <div
            class="vo-container"
            role="dialog"
            aria-modal="true"
            aria-label="Vehicle Highlights"
        >
            <div class="vo-header">
                <div class="vo-header-left">
                    <!--<h2>Your Vehicles &mdash;</h2>-->
                    <div class="vo-advisor">
                        <img class="vo-avatar" src="${this.advisor.avatar}" alt="${this.advisor.name}" />
                        <span>shared by ${this.advisor.name}</span>
                    </div>
                </div>
                <button class="vo-close" aria-label="Close">&times;</button>
            </div>
            <div class="vo-cards">${this.data.map(this.card).join("")}</div>
            <div class="vo-disclaimer">
                <div class="vo-disclaimer-content">
                    Estimated lease payments are based on a range of possible down payments, from $8,000 to $0, and are
                    provided for illustrative purposes only. Actual payments will vary depending on final vehicle price,
                    agreed-upon lease terms, and down payment selected. Estimates exclude applicable taxes, title,
                    registration, license, and documentation fees. They may also reflect credits, incentives, or
                    rebates, the availability and amount of which are subject to change and BMW Center participation.
                    Please consult your selected BMW Center for final terms and eligibility for any offers.
                </div>
            </div>
            <div class="vo-footer">
                <button class="vo-cta-btn" disabled>Share with ${this.advisor.name}</button>
                <button class="vo-disclaimer-toggle">Show Disclaimer</button>
            </div>
        </div>`;
        document.body.appendChild(this.overlay);

        /* interactions */
        this.overlay.querySelector(".vo-close")!.addEventListener("click", () => this.close());
        this.overlay.addEventListener("click", (e) => {
            if (e.target === this.overlay) this.close();
        });
        const ctaBtn = this.overlay.querySelector(".vo-cta-btn");
        this.overlay.querySelectorAll(".vo-card").forEach((card) => {
            card.addEventListener("click", (e) => {
                // ignore clicks on the two CTA buttons
                if ((e.target as any).closest(".vo-links")) return;

                const id = card.getAttribute("data-id");
                card.classList.toggle("selected");

                if (card.classList.contains("selected")) {
                    this.selectedIds.add(id!);
                } else {
                    this.selectedIds.delete(id!);
                }

                if (this.selectedIds.size) {
                    ctaBtn?.classList.add("enabled");
                    ctaBtn?.removeAttribute("disabled");
                } else {
                    ctaBtn?.classList.remove("enabled");
                    ctaBtn?.setAttribute("disabled", "");
                }
            });
        });

        ctaBtn?.addEventListener("click", () => {
            location.href = `sms:${this.advisor.phone}?body=Hi%20${
                this.advisor.nickname
            },%20I%20like%20the%20following%20${this.selectedIds.size === 1 ? "VIN" : "VINs"}:%20${[
                ...this.selectedIds,
            ].join(",%20")}`;
        });

        this.prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        // Fade each image in when it finishes loading
        this.overlay.querySelectorAll(".vo-img").forEach((wrapper) => {
            const img = wrapper.querySelector("img");
            const done = () => wrapper.classList.add("loaded");
            // already cached? (e.g. back‑button)
            if (img?.complete && img?.naturalWidth) done();
            else {
                img?.addEventListener("load", done, { once: true });
                img?.addEventListener("error", done, { once: true }); // still reveal if 404
            }
        });

        // Add click event listener for disclaimer toggle
        const disclaimerToggle = this.overlay?.querySelector(".vo-disclaimer-toggle");
        if (disclaimerToggle) {
            console.log("Disclaimer toggle", disclaimerToggle);
            disclaimerToggle.addEventListener("click", (e) => {
                console.log("Toggle Disclaimer", e);
                this.toggleDisclaimer();
            });
        }
    }

    card = ({ id, imageUrl, title, brochureUrl, leaseRangeText, msrpText }) => /* HTML */ ` <div
        class="vo-card"
        data-id="${id}"
    >
        <div class="vo-img">
            <div class="vo-img-placeholder"></div>
            <img src="${imageUrl}" alt="${title}" />
        </div>
        <span class="vo-check">✓</span>
        <div class="vo-card-content">
            <div class="vo-desc">${leaseRangeText}</div>
            <div class="vo-desc mb-16">${msrpText}</div>
            <div class="vo-links">
                <a class="brochure" href="${brochureUrl}" target="_blank" rel="noopener">BMW Digital Brochure</a>
            </div>
        </div>
    </div>`;

    close() {
        this.overlay?.classList.remove("vo-enter");
        this.overlay?.addEventListener(
            "transitionend",
            () => {
                this.overlay?.remove();
                document.body.style.overflow = this.prevOverflow || "";
            },
            { once: true },
        );
    }
}
